import React, { FC, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Link,
  Typography,
} from '@material-ui/core'
import { TolNotification } from '../../../../types/tolOperations'
import { getNotificationPublishStatus } from '../shared/periodStatus'

const TYPE_ICONS = {
  information: '/assets/images/information.svg',
  warning: '/assets/images/warning.svg',
} as const

const CLOSE_ICON = '/assets/images/close.svg'

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    maxWidth: 600,
    width: '100%',
  },
  previewContainer: {
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(3),
    minHeight: 300,
  },
  phoneFrame: {
    maxWidth: 375,
    margin: '0 auto',
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  phoneHeader: {
    backgroundColor: '#fff',
    padding: theme.spacing(2),
    borderBottom: '1px solid #e0e0e0',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  notificationWrapper: {
    backgroundColor: '#fff',
    borderTop: '1px solid #e0e0e0',
  },
  notificationCard: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #e0e0e0',
    padding: 8,
  },
  iconWrapper: {
    flexBasis: 46,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconImage: {
    width: 20,
    height: 20,
  },
  contentWrapper: {
    flexBasis: 'auto',
    width: '100%',
    display: 'inline-block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  title: {
    fontSize: 15,
    color: '#0E73DC',
    textDecoration: 'underline',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  noteMessage: {
    backgroundColor: '#fff3e0',
    border: '1px solid #ff9800',
    borderRadius: 4,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}))

interface NotificationPreviewModalProps {
  open: boolean
  notifications: TolNotification[]
  onClose: () => void
}

/** ToLホーム画面と同様に、同時に表示されるお知らせは最大2件 */
const MAX_VISIBLE_NOTIFICATIONS = 2

/**
 * プレビューに表示するお知らせを返す。
 * 公開終了（表示終了日時が過去）と既読済みを除外し、配列順の先頭から最大2件を表示する
 */
export const getVisiblePreviewNotifications = (
  notifications: TolNotification[],
  dismissedIds: string[],
  now = new Date(),
): TolNotification[] =>
  notifications
    .filter(notification => getNotificationPublishStatus(notification, now) !== 'ended')
    .filter(notification => !dismissedIds.includes(notification.id))
    .slice(0, MAX_VISIBLE_NOTIFICATIONS)

export const NotificationPreviewModal: FC<NotificationPreviewModalProps> = ({ open, notifications, onClose }) => {
  const classes = useStyles()
  const [dismissedIds, setDismissedIds] = useState<string[]>([])

  // モーダルを開き直したときは既読状態をリセットする
  useEffect(() => {
    if (open) {
      setDismissedIds([])
    }
  }, [open])

  const displayNotifications = getVisiblePreviewNotifications(notifications, dismissedIds)

  const handleDismiss = (notificationId: string) => {
    setDismissedIds(prev => [...prev, notificationId])
  }

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography>
        <Typography variant="h6" component="h2">
          プレビュー
        </Typography>
        <Typography variant="body2" color="textSecondary">
          ToLホーム画面での表示イメージ
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box className={classes.previewContainer}>
          <Box className={classes.phoneFrame}>
            {displayNotifications.length > 0 && (
              <>
                <Box className={classes.phoneHeader}>
                  <Typography variant="body2">ToL ホーム画面</Typography>
                </Box>
                <Box className={classes.notificationWrapper}>
                  {displayNotifications.map(notification => (
                    <Box key={notification.id} className={classes.notificationCard}>
                      <Box className={classes.iconWrapper}>
                        <img
                          src={TYPE_ICONS[notification.type]}
                          alt={notification.type}
                          className={classes.iconImage}
                        />
                      </Box>
                      <Box className={classes.contentWrapper}>
                        <Link
                          href={notification.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.title}
                        >
                          {notification.title}
                        </Link>
                      </Box>
                      <Box className={classes.iconWrapper}>
                        <IconButton size="small" aria-label="既読にする" onClick={() => handleDismiss(notification.id)}>
                          <img src={CLOSE_ICON} alt="" className={classes.iconImage} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>

          {displayNotifications.length === 0 && (
            <Box className={classes.emptyState}>
              <Typography variant="body1">表示可能なお知らせがありません</Typography>
            </Box>
          )}

          <Box className={classes.noteMessage}>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              ・上から2件が表示されます。
              <br />
              ・×ボタンを押下した場合、上から3番目のお知らせが表示されます
              <br />
              ・表示開始日時が未来のお知らせも表示されます
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  )
}
